import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowUpRight, ArrowDownLeft, FilePlus, Upload, Download,
  Building2, Calendar, ChevronRight, Eye, List, FileText, AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { API } from '../../utils/api';
import { useAuthContext } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { OptionalDetailsBadge } from '../../components/GuestDetails';

const PAYMENT_METHODS = [
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'SWISH', label: 'Swish' },
  { value: 'CASH', label: 'Cash' },
  { value: 'INVOICE', label: 'Invoice' },
  { value: 'CARD', label: 'Card' },
  { value: 'OTHER', label: 'Other' },
];

export function BusinessHome({ onNavigate }) {
  const { t } = useTranslation();
  const { headers, user } = useAuthContext();
  const [dashboard, setDashboard] = useState(null);
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const [subAccounts, setSubAccounts] = useState([]);
  const [categories, setCategories] = useState([]);

  // Manual entry dialog
  const [entryOpen, setEntryOpen] = useState(false);
  const [entryForm, setEntryForm] = useState({
    recipient_name: '', amount: '', description: '', invoice_ref: '',
    category: '', payment_method: 'BANK_TRANSFER', date: new Date().toISOString().split('T')[0],
    sub_account_id: '', notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  // CSV import dialog
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvResult, setCsvResult] = useState(null);

  // Pending Swish payment confirmation (legacy)
  const [pendingTxn, setPendingTxn] = useState(null);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/business/dashboard?period=${period}`, { headers });
      setDashboard(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [headers, period]);

  const fetchSubAccounts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/business/sub-accounts`, { headers });
      setSubAccounts(res.data.items || []);
    } catch (e) { console.error(e); }
  }, [headers]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/business/settings/categories`, { headers });
      setCategories(res.data.categories || []);
    } catch { setCategories(['Salary', 'Vendor', 'Supplies', 'Event', 'Other']); }
  }, [headers]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);
  useEffect(() => { fetchSubAccounts(); }, [fetchSubAccounts]);
  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  // WebSocket
  useEffect(() => {
    if (!user?.id) return;
    const wsUrl = API.replace(/^http/, 'ws') + '/ws';
    let ws;
    try {
      ws = new WebSocket(wsUrl);
      ws.onopen = () => ws.send(JSON.stringify({ type: 'subscribe', user_id: user.id }));
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'dashboard_update' || data.type === 'payment_received') {
            fetchDashboard();
            fetchSubAccounts();
          }
        } catch {}
      };
    } catch {}
    return () => { if (ws) ws.close(); };
  }, [user?.id, fetchDashboard, fetchSubAccounts]);

  const resetEntryForm = () => {
    setEntryForm({
      recipient_name: '', amount: '', description: '', invoice_ref: '',
      category: '', payment_method: 'BANK_TRANSFER', date: new Date().toISOString().split('T')[0],
      sub_account_id: '', notes: '',
    });
  };

  const handleCreateEntry = async () => {
    if (!entryForm.recipient_name.trim()) return toast.error('Recipient name is required');
    if (!entryForm.amount || parseFloat(entryForm.amount) <= 0) return toast.error('Enter a valid amount');
    if (!entryForm.date) return toast.error('Date is required');
    try {
      setSubmitting(true);
      await axios.post(`${API}/business/manual-transaction`, {
        recipient_name: entryForm.recipient_name.trim(),
        amount: parseFloat(entryForm.amount),
        description: entryForm.description || undefined,
        invoice_ref: entryForm.invoice_ref || undefined,
        category: entryForm.category || undefined,
        payment_method: entryForm.payment_method,
        date: entryForm.date,
        sub_account_id: entryForm.sub_account_id || undefined,
        notes: entryForm.notes || undefined,
      }, { headers });
      toast.success('Transaction recorded');
      setEntryOpen(false);
      resetEntryForm();
      fetchDashboard();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create transaction');
    } finally { setSubmitting(false); }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/business/manual-transaction/csv-template`, { headers });
      const blob = new Blob([res.data.csv_data], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = res.data.filename || 'paymig_import_template.csv';
      link.click();
      toast.success('Template downloaded');
    } catch { toast.error('Failed to download template'); }
  };

  const handleCsvUpload = async () => {
    if (!csvFile) return toast.error('Select a CSV file first');
    setCsvImporting(true);
    setCsvResult(null);
    try {
      const text = await csvFile.text();
      const res = await axios.post(`${API}/business/manual-transaction/csv-import`, { csv_data: text }, { headers });
      setCsvResult(res.data);
      if (res.data.imported > 0) {
        toast.success(`${res.data.imported} transactions imported`);
        fetchDashboard();
      }
      if (res.data.errors?.length) {
        toast.warning(`${res.data.errors.length} rows had errors`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally { setCsvImporting(false); }
  };

  const confirmPayment = async () => {
    if (!pendingTxn) return;
    try {
      await axios.post(`${API}/business/payment/${pendingTxn.id}/confirm`, {}, { headers });
      toast.success('Payment confirmed');
      setPendingTxn(null);
      fetchDashboard();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const cancelPayment = async () => {
    if (!pendingTxn) return;
    try {
      await axios.post(`${API}/business/payment/${pendingTxn.id}/cancel`, {}, { headers });
      toast.info('Payment cancelled');
      setPendingTxn(null);
      fetchDashboard();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="text-center py-8" data-testid="business-home-loading">Loading dashboard...</div>;
  if (!dashboard) return <div className="text-center py-8">Failed to load dashboard</div>;

  return (
    <div className="space-y-5" data-testid="business-home">
      {/* Period selector */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">{t('business.dashboard')}</h2>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-32" data-testid="period-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="today">{t('common.today')}</SelectItem>
            <SelectItem value="week">{t('common.thisWeek')}</SelectItem>
            <SelectItem value="month">{t('common.thisMonth')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Pending Swish payment confirmation banner (legacy) */}
      {pendingTxn && (
        <Card className="border-amber-300 bg-amber-50" data-testid="pending-payment-confirm">
          <CardContent className="p-4">
            <p className="text-sm font-medium text-amber-900 mb-1">Did you complete this payment in Swish?</p>
            <p className="text-xs text-amber-700 mb-3">{pendingTxn.amount} SEK to {pendingTxn.recipient}</p>
            <div className="flex gap-2">
              <Button size="sm" onClick={confirmPayment} data-testid="confirm-payment-btn">Yes, completed</Button>
              <Button size="sm" variant="outline" onClick={cancelPayment} data-testid="cancel-payment-btn">No, cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 gap-4">
        <Card data-testid="revenue-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <ArrowDownLeft className="h-4 w-4 text-emerald-600" />
              <span className="text-xs text-muted-foreground">{t('business.revenue')}</span>
            </div>
            <p className="text-2xl font-bold text-emerald-700" data-testid="revenue-value">
              {dashboard.revenue.total.toLocaleString()} SEK
            </p>
            <p className="text-xs text-muted-foreground">{dashboard.revenue.count} transactions</p>
          </CardContent>
        </Card>
        <Card data-testid="payments-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <ArrowUpRight className="h-4 w-4 text-rose-600" />
              <span className="text-xs text-muted-foreground">{t('business.paymentsMade')}</span>
            </div>
            <p className="text-2xl font-bold text-rose-700" data-testid="payments-value">
              {dashboard.payments.total.toLocaleString()} SEK
            </p>
            <p className="text-xs text-muted-foreground">{dashboard.payments.count} transactions</p>
          </CardContent>
        </Card>
      </div>

      {/* Enter Payment + Import CSV Buttons */}
      <div className="flex gap-3">
        <Dialog open={entryOpen} onOpenChange={setEntryOpen}>
          <DialogTrigger asChild>
            <Button className="flex-1 h-14 text-base gap-2" data-testid="business-enter-payment-btn">
              <FilePlus className="h-5 w-5" /> Enter Payment
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Record a Payment</DialogTitle>
              <DialogDescription>Enter a payment already made by your organization.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="space-y-1">
                <Label>Recipient Name *</Label>
                <Input data-testid="entry-recipient" placeholder="Vendor AB"
                  value={entryForm.recipient_name} onChange={e => setEntryForm(p => ({ ...p, recipient_name: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Amount (SEK) *</Label>
                  <Input data-testid="entry-amount" type="number" placeholder="15000" min="0"
                    value={entryForm.amount} onChange={e => setEntryForm(p => ({ ...p, amount: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label>Date *</Label>
                  <Input data-testid="entry-date" type="date"
                    value={entryForm.date} onChange={e => setEntryForm(p => ({ ...p, date: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Payment Method *</Label>
                  <Select value={entryForm.payment_method} onValueChange={v => setEntryForm(p => ({ ...p, payment_method: v }))}>
                    <SelectTrigger data-testid="entry-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Category</Label>
                  <Select value={entryForm.category} onValueChange={v => setEntryForm(p => ({ ...p, category: v === 'none' ? '' : v }))}>
                    <SelectTrigger data-testid="entry-category"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1">
                <Label>Invoice Reference</Label>
                <Input data-testid="entry-invoice-ref" placeholder="INV-2026-001"
                  value={entryForm.invoice_ref} onChange={e => setEntryForm(p => ({ ...p, invoice_ref: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Allocate to Sub-Account</Label>
                <Select value={entryForm.sub_account_id} onValueChange={v => setEntryForm(p => ({ ...p, sub_account_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger data-testid="entry-sub-account"><SelectValue placeholder="No allocation" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No allocation</SelectItem>
                    {subAccounts.map(sa => (
                      <SelectItem key={sa.id} value={sa.id}>
                        {sa.type === 'EVENT' ? '📅' : '🏢'} {sa.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Description</Label>
                <Input data-testid="entry-description" placeholder="Office supplies Q2"
                  value={entryForm.description} onChange={e => setEntryForm(p => ({ ...p, description: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Internal Notes</Label>
                <Textarea data-testid="entry-notes" placeholder="Approved by CFO" rows={2}
                  value={entryForm.notes} onChange={e => setEntryForm(p => ({ ...p, notes: e.target.value }))} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setEntryOpen(false); resetEntryForm(); }} data-testid="entry-cancel">Cancel</Button>
              <Button onClick={handleCreateEntry} disabled={submitting} data-testid="entry-submit">
                <FilePlus className="h-4 w-4 mr-2" />{submitting ? 'Saving...' : 'Record Payment'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Import CSV Button */}
        <Dialog open={csvOpen} onOpenChange={(o) => { setCsvOpen(o); if (!o) { setCsvFile(null); setCsvResult(null); } }}>
          <DialogTrigger asChild>
            <Button variant="outline" className="h-14 gap-2" data-testid="business-import-csv-btn">
              <Upload className="h-5 w-5" /> Import
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Import Transactions</DialogTitle>
              <DialogDescription>Upload a CSV file to bulk-import manual transactions.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <Button variant="outline" className="w-full gap-2" onClick={handleDownloadTemplate} data-testid="csv-download-template">
                <Download className="h-4 w-4" /> Download CSV Template
              </Button>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                <input type="file" accept=".csv" className="hidden" id="csv-upload-input"
                  onChange={e => { setCsvFile(e.target.files?.[0] || null); setCsvResult(null); }} />
                <label htmlFor="csv-upload-input" className="cursor-pointer">
                  <FileText className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">{csvFile ? csvFile.name : 'Click to select a CSV file'}</p>
                </label>
              </div>
              {csvResult && (
                <div className="space-y-2" data-testid="csv-result">
                  <div className="flex gap-3 text-sm">
                    <span className="text-emerald-700 font-medium">{csvResult.imported} imported</span>
                    {csvResult.skipped > 0 && <span className="text-amber-700">{csvResult.skipped} skipped</span>}
                    {csvResult.errors?.length > 0 && <span className="text-rose-700">{csvResult.errors.length} errors</span>}
                  </div>
                  {csvResult.errors?.length > 0 && (
                    <div className="max-h-32 overflow-y-auto text-xs space-y-1 bg-rose-50 p-2 rounded">
                      {csvResult.errors.map((err, i) => (
                        <div key={i} className="flex gap-1 text-rose-700">
                          <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                          <span>Row {err.row}: {err.error}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setCsvOpen(false); setCsvFile(null); setCsvResult(null); }}>Close</Button>
              <Button onClick={handleCsvUpload} disabled={!csvFile || csvImporting} data-testid="csv-upload-btn">
                <Upload className="h-4 w-4 mr-2" />{csvImporting ? 'Importing...' : 'Upload & Import'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Recent Transactions */}
      <Card data-testid="recent-transactions-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">{t('business.recentTransactions')}</CardTitle>
        </CardHeader>
        <CardContent>
          {dashboard.recent_transactions.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4" data-testid="no-recent-txns">No recent transactions</p>
          ) : (
            <div className="space-y-2">
              {dashboard.recent_transactions.slice(0, 5).map((txn, i) => (
                <div key={txn.trans_id || i} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg" data-testid={`recent-txn-${i}`}>
                  <div>
                    <p className="text-sm font-medium">{txn.from_name || txn.to_name || 'Unknown'}</p>
                    <p className="text-xs text-muted-foreground">{txn.trans_id ? `#${txn.trans_id}` : ''} {txn.description || ''}</p>
                    <div className="flex gap-1 mt-0.5">
                      {txn.sub_account_name && <Badge variant="outline" className="text-[10px]">{txn.sub_account_name}</Badge>}
                      {txn.entry_type === 'MANUAL' && <Badge variant="secondary" className="text-[10px]">Manual</Badge>}
                    </div>
                  </div>
                  <p className={`text-sm font-semibold ${txn.direction === 'SENT' ? 'text-rose-600' : 'text-emerald-600'}`}>
                    {txn.direction === 'SENT' ? '-' : '+'}{txn.amount} SEK
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sub-accounts summary */}
      <Card data-testid="sub-accounts-summary">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              {t('business.subAccounts')} ({subAccounts.length})
            </CardTitle>
            {dashboard.pending_invitations > 0 && (
              <Badge variant="outline" className="text-orange-600 border-orange-300">
                {dashboard.pending_invitations} pending
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {subAccounts.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4" data-testid="no-sub-accounts">No sub-accounts yet</p>
          ) : (
            <div className="space-y-1.5">
              {subAccounts.slice(0, 6).map(sa => (
                <div key={sa.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg" data-testid={`sub-account-${sa.id}`}>
                  {sa.type === 'EVENT' ? <Calendar className="h-4 w-4 text-blue-500 shrink-0" /> : <Building2 className="h-4 w-4 text-purple-500 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <p className="text-sm font-medium truncate">{sa.name}</p>
                      <OptionalDetailsBadge sa={sa} testId={`home-optional-details-${sa.id}`} />
                    </div>
                    <div className="flex items-center gap-2 text-[11px]">
                      <span className="text-emerald-700" data-testid={`sa-received-${sa.id}`}>+{(sa.total_received || 0).toLocaleString()}</span>
                      <span className="text-rose-700" data-testid={`sa-sent-${sa.id}`}>-{(sa.total_sent || 0).toLocaleString()}</span>
                      <span className="text-muted-foreground">SEK</span>
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="sm" className="h-6 text-[10px] px-1.5"
                      onClick={() => onNavigate && onNavigate('sub-accounts', { subAccountId: sa.id })}
                      data-testid={`sa-details-btn-${sa.id}`}>
                      <Eye className="h-3 w-3" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-6 text-[10px] px-1.5"
                      onClick={() => onNavigate && onNavigate('history', { subAccountFilter: sa.id })}
                      data-testid={`sa-txns-btn-${sa.id}`}>
                      <List className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
